import { useState } from "react";
import { Button } from "@/ui/components/ui/button";
import { Input } from "@/ui/components/ui/input";
import { Label } from "@/ui/components/ui/label";
import { useToast } from "@/controllers/hooks/use-toast";
import { Loader2, Shield, ArrowLeft } from "lucide-react";
import { supabase } from "@/infra/supabase/client";

export default function RecoverForm() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const { toast } = useToast();

  const handleRecover = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        toast({
          title: "Erro na recuperação",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Email enviado!",
          description: `Enviamos as instruções de recuperação para ${email}`,
          className: "toast-success",
        });
        setIsEmailSent(true);
      }
    } catch (error: unknown) {
      toast({
        title: "Erro inesperado",
        description: "Tente novamente em alguns instantes.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isEmailSent) {
    return (
      <div className="space-y-6 text-center">
        <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto">
          <Shield className="w-8 h-8 text-accent-800" />
        </div>

        <div className="space-y-2">
          <h3 className="text-xl font-semibold text-foreground">
            Email Enviado!
          </h3>
          <p className="text-foreground-secondary">
            Enviamos as instruções de recuperação para
            <br />
            <span className="text-accent-800 font-medium">{email}</span>
          </p>
        </div>

        <div className="bg-accent-50 border border-accent-200 rounded-lg p-4 hover-glow">
          <div className="flex items-center space-x-2 text-accent-800 mb-2">
            <Shield className="w-4 h-4" />
            <span className="text-sm font-medium">Processo Seguro</span>
          </div>
          <p className="text-xs text-foreground-secondary">
            O link de recuperação é válido por 24 horas e pode ser usado apenas
            uma vez por motivos de segurança.
          </p>
        </div>

        <div className="space-y-3">
          <Button
            type="button"
            variant="premium"
            size="lg"
            className="w-full"
            onClick={() => setIsEmailSent(false)}
          >
            Enviar Novamente
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="lg"
            className="w-full"
            asChild
          >
            <a href="/login">
              <ArrowLeft className="w-4 h-4" />
              Voltar ao Login
            </a>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <form onSubmit={handleRecover} className="space-y-4">
        <div className="space-y-2">
          <Label
            htmlFor="email"
            className="text-foreground-secondary font-medium"
          >
            Email da Conta
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="seu@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="h-12 bg-white/60 border-black/[0.08] text-neutral-900 placeholder:text-neutral-400 rounded-xl transition-all hover:border-black/[0.12] focus:border-accent-800/50 focus:ring-2 focus:ring-accent-800/10"
          />
          <p className="text-xs text-foreground-muted">
            Digite o email associado à sua conta para receber as instruções de
            recuperação.
          </p>
        </div>

        <Button
          type="submit"
          variant="premium"
          size="lg"
          className="w-full"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Enviando código...
            </>
          ) : (
            "Enviar Código de Recuperação"
          )}
        </Button>
      </form>

      <div className="bg-accent-50 border border-accent-200 rounded-lg p-4 hover-glow">
        <div className="flex items-center space-x-2 text-accent-800 mb-2">
          <Shield className="w-4 h-4" />
          <span className="text-sm font-medium">
            Processo Seguro de Recuperação
          </span>
        </div>
        <p className="text-xs text-foreground-secondary">
          Suas informações estão protegidas. O processo de recuperação é
          criptografado e segue as melhores práticas de segurança.
        </p>
      </div>

      <div className="text-center">
        <Button type="button" variant="ghost" asChild>
          <a
            href="/login"
            className="text-foreground-secondary hover:text-accent-800 transition-smooth"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar ao Login
          </a>
        </Button>
      </div>
    </div>
  );
}
